import React, { useEffect, useState, Fragment } from 'react';
import {
  Checkbox, Tooltip, Select, DatePicker,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './style.scss';
import PRZSelect from '../../Common/UI/PRZSelect';

const { Option } = Select;

const ScheduledReport = ({
  setScheduledReportData, disabled, allowedDurations, alwaysOn, schedulePrefillData
}) => {
  const [isScheduledReport, setIsScheduledReport] = useState(alwaysOn || false);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState(allowedDurations?.[0]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [isUserReady, setIsUserReady] = useState(null);

  function validateScheduledReport(isScheduledReportData, selectedTimeData, selectedFrequencyData, selectedDateData, selectedDaysData) {
    const missingFields = [];

    if (!isScheduledReportData) {
      return { isValid: false, missingFields }; // If the report is not scheduled, no need to validate further.
    }

    switch (selectedFrequencyData) {
      case 'DAILY':
        if (!selectedTimeData) {
          missingFields.push('selectedTime');
        }
        break;
      case 'WEEKLY':
        if (!selectedDaysData || selectedDaysData.length === 0) {
          missingFields.push('selectedDays');
        }
        break;
      case 'MONTHLY':
        if (!selectedDateData) {
          missingFields.push('selectedDate');
        }
        if (!selectedTimeData) {
          missingFields.push('selectedTime');
        }
        break;
      case 'HOURLY':
        // No additional fields are required for hourly frequency.
        break;
      default:
        missingFields.push('selectedFrequency'); // If the frequency is not one of the expected values.
    }

    const isValid = missingFields.length === 0;
    return { isValid, missingFields };
  }

  // use effect to prefill data
  useEffect(() => {
    if (schedulePrefillData && !isUserReady) {
      setIsScheduledReport(schedulePrefillData.isScheduledReport);
      setSelectedTime(schedulePrefillData.selectedTime);
      setSelectedFrequency(schedulePrefillData.selectedFrequency);
      setSelectedDate(schedulePrefillData.selectedDate ? dayjs(schedulePrefillData.selectedDate) : null);
      setSelectedDays(schedulePrefillData.selectedDays);
      setIsUserReady(true);
    }
  }, [schedulePrefillData]);

  useEffect(() => {
    const { isValid, missingFields } = validateScheduledReport(
      isScheduledReport, selectedTime, selectedFrequency, selectedDate, selectedDays,
    );
    setScheduledReportData({
      isScheduledReport,
      selectedTime,
      selectedFrequency,
      selectedDate,
      selectedDays,
      isValid,
      missingFields,
    });
  }, [isScheduledReport, selectedTime, selectedFrequency, selectedDate, selectedDays, setScheduledReportData]);

  const handleCheckboxChange = (e) => {
    setIsScheduledReport(e.target.checked);
    if (!e.target.checked) {
      setSelectedTime('');
      setSelectedFrequency('');
      setSelectedDate(null);
      setSelectedDays([]);
    }
  };

  const handleTimeChange = (value) => {
    setSelectedTime(value);
  };

  const handleFrequencyChange = (value) => {
    setSelectedFrequency(value);
    setSelectedDays([]);
    setSelectedDate(null);
  };

  const handleDayChange = (day, checked) => {
    const newSelectedDays = checked
      ? [...selectedDays, day]
      : selectedDays.filter((d) => d !== day);
    setSelectedDays(newSelectedDays);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };
  const disabledDate = (current) => current && current < dayjs().startOf('day');

  return (
    <div style={{ marginTop: '10px' }} className="scheduler-wrapper">
      {!alwaysOn && (
        <Fragment>
          <Checkbox
            checked={isScheduledReport}
            onChange={handleCheckboxChange}
            disabled={disabled}
            style={{
              marginBottom: '4px',
            }}
          />
          <span
            style={{
              fontWeight: '500',
              fontSize: '12px',
              marginLeft: '5px',
            }}
          >
            Scheduled this report
            <Tooltip title="If you opt for this option, we'll automatically send you tailored reports based on your preferences.">
              <InfoCircleOutlined
                style={{
                  marginLeft: '5px',
                  color: 'rgb(45, 124, 247)',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          </span>
        </Fragment>
      )}
      {isScheduledReport && (
        <div
          className="scheduler-inputs"
        >
          <div className="flex-display flex-align-c" style={{ flexDirection: 'column', gap: '10px' }}>
            <div className="scheduler-input__wrapper" style={{ width: '100%' }}>
              <label className="orgFormLabel">
                Select Frequency
                {' '}
                <span style={{ color: 'red' }}>{'  *'}</span>
              </label>
              <PRZSelect
                className="input"
                placeholder="Select Frequency"
                onChange={handleFrequencyChange}
                value={selectedFrequency}
              >
                {allowedDurations.includes('HOURLY') && <Option value="HOURLY">Hourly</Option>}
                {allowedDurations.includes('DAILY') && <Option value="DAILY">Daily</Option>}
                {allowedDurations.includes('ONCE') && <Option value="ONCE">Send Once</Option>}
                {allowedDurations.includes('WEEKLY') && <Option value="WEEKLY">Weekly</Option>}
                {allowedDurations.includes('MONTHLY') && <Option value="MONTHLY">Monthly</Option>}
              </PRZSelect>
            </div>
            {(selectedFrequency !== 'HOURLY') && (
              <div className="scheduler-input__wrapper" style={{ width: '100%' }}>
                <label className="orgFormLabel">
                  Select Time
                  {' '}
                  <span style={{ color: 'red' }}>{'  *'}</span>
                </label>
                <PRZSelect
                  className="input"
                  placeholder="Select Time"
                  onChange={handleTimeChange}
                  value={selectedTime}
                >
                  {[...Array(24)].map((_, i) => (
                    <Option key={i} value={`${i}:00`}>{`${i === 0 ? 12 : (i > 12 ? i - 12 : i)}:00 ${i >= 12 ? 'PM' : 'AM'}`}</Option>
                  ))}
                </PRZSelect>
              </div>
            )}
            {(selectedFrequency === 'ONCE' || selectedFrequency === 'MONTHLY') && (
              <div className="scheduler-input__wrapper" style={{ width: '100%' }}>
                <label className="orgFormLabel">
                  Select Date
                  {' '}
                  <span style={{ color: 'red' }}>{'  *'}</span>
                </label>
                <DatePicker onChange={handleDateChange} value={selectedDate} className="date-picker" disabledDate={disabledDate} style={{ width: '100%' }} />
              </div>
            )}
            {selectedFrequency === 'WEEKLY' && (
            <div className="scheduler-input__wrapper" style={{ width: '100%' }}>
              <label className="orgFormLabel">
                Select Days
                {' '}
                <span style={{ color: 'red' }}>{'  *'}</span>
              </label>
              <div className="flex-display" style={{ flexWrap: 'wrap' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <Checkbox
                    key={day}
                    checked={selectedDays.includes(day)}
                    onChange={(e) => handleDayChange(day, e.target.checked)}
                    style={{ marginRight: '10px', marginBottom: '5px' }}
                  >
                    {day}
                  </Checkbox>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledReport;
